import React, { useMemo } from 'react';
import EventTable from './EventTable';
import { sanitizeHtml } from '../../../shared/security/sanitizeHtml';
import { safeParseJson } from '../../../shared/security/safeJson';

/**
 * Starred Events panelinde FVALUE içeriğini birebir preview eden bileşen.
 * RightPanelBody'deki tüm render tiplerini destekler.
 */
const StarredEventPreview = ({ FVALUE, FNAME }) => {
  const preview = useMemo(() => {
    if (!FVALUE || typeof FVALUE !== 'string') return null;

    // --- YouTube ---
    if (String(FNAME || '').toUpperCase() === 'YOUTUBE') {
      const m = FVALUE.match(
        /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/shorts\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
      );
      const videoId = m?.[1] || (/^[a-zA-Z0-9_-]{11}$/.test(FVALUE.trim()) ? FVALUE.trim() : null);
      if (videoId) {
        return { type: 'youtube', videoId, href: FVALUE };
      }
      return { type: 'link', href: FVALUE };
    }

    // --- JSON parse dene ---
    const parsed = safeParseJson(FVALUE, 200000);
    if (parsed.ok) {
      const val = parsed.value;

      // Treestructure
      if (FNAME === 'TREESTRUCTURE') {
        return { type: 'treestructure' };
      }

      // Workflow / Flowchart
      if (val?.workflow?.id) {
        return { type: 'workflow', workflowId: val.workflow.id };
      }

      // Tablo
      if (val?.table_content && val?.table_header) {
        return {
          type: 'table',
          tableContent: val.table_content,
          tableHeader: val.table_header,
        };
      }

      // Buton dizisi
      if (Array.isArray(val) && val.some((i) => i.DESC && i.VALUE)) {
        return { type: 'buttons', items: val };
      }

      // Genel JSON obje/dizi
      return { type: 'json', text: JSON.stringify(val, null, 2) };
    }

    // --- HTML ---
    const isHtml = /<\/?[a-z][\s\S]*>/i.test(FVALUE);
    if (isHtml) {
      return { type: 'html', html: sanitizeHtml(FVALUE) };
    }

    // --- Düz metin ---
    return { type: 'text', text: FVALUE };
  }, [FVALUE, FNAME]);

  if (!preview) return null;

  /* ── YouTube ── */
  if (preview.type === 'youtube') {
    return (
      <div className="overflow-hidden rounded-lg border" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
        <img
          src={`https://img.youtube.com/vi/${preview.videoId}/mqdefault.jpg`}
          alt="YouTube thumbnail"
          className="w-full object-cover"
          style={{ maxHeight: 120 }}
          onError={(e) => { e.target.style.display = 'none'; }}
        />
        <div className="flex items-center gap-2 px-2.5 py-1.5" style={{ background: 'rgba(0,0,0,0.3)' }}>
          <span style={{ fontSize: 11, color: '#ff4444', fontWeight: 700 }}>▶</span>
          <a
            href={preview.href}
            target="_blank"
            rel="noreferrer"
            className="truncate text-[11px] font-medium text-blue-400 hover:text-blue-300"
          >
            YouTube'da İzle
          </a>
        </div>
      </div>
    );
  }

  /* ── Link ── */
  if (preview.type === 'link') {
    return (
      <a
        href={preview.href}
        target="_blank"
        rel="noreferrer"
        className="block truncate text-[11px] text-blue-400 underline hover:text-blue-300"
      >
        {preview.href}
      </a>
    );
  }

  /* ── Workflow badge ── */
  if (preview.type === 'workflow') {
    return (
      <div
        className="flex items-center gap-2 rounded-md px-2.5 py-2 text-[11px]"
        style={{ background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.25)', color: '#a5b4fc' }}
      >
        <svg className="h-3.5 w-3.5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path d="M3 3h7v7H3zM14 3h7v7h-7zM14 14h7v7h-7zM3 14h7v7H3z" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <span className="font-medium">Flowchart</span>
        <span className="ml-auto font-mono text-[9px] opacity-60">{String(preview.workflowId).slice(0, 12)}</span>
      </div>
    );
  }

  /* ── TreeStructure badge ── */
  if (preview.type === 'treestructure') {
    return (
      <div
        className="flex items-center gap-2 rounded-md px-2.5 py-2 text-[11px]"
        style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.25)', color: '#6ee7b7' }}
      >
        <svg className="h-3.5 w-3.5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path d="M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <span className="font-medium">Ağaç Yapısı</span>
      </div>
    );
  }

  /* ── Tablo — birebir EventTable ── */
  if (preview.type === 'table') {
    return (
      <div className="overflow-hidden rounded-md" style={{ border: '1px solid rgba(255,255,255,0.1)' }}>
        <EventTable
          tableContent={preview.tableContent}
          tableHeader={preview.tableHeader}
          showSelectButton={false}
          eventFNAME={FNAME}
        />
      </div>
    );
  }

  /* ── Butonlar ── */
  if (preview.type === 'buttons') {
    return (
      <div className="flex flex-wrap gap-1.5">
        {preview.items.map((item, i) => (
          <span
            key={i}
            className="rounded-md border px-2 py-1 text-[10.5px] font-medium"
            style={{
              background: 'rgba(255,255,255,0.06)',
              borderColor: 'rgba(255,255,255,0.12)',
              color: '#cbd5e1',
            }}
          >
            {item.DESC}
          </span>
        ))}
      </div>
    );
  }

  /* ── HTML ── */
  if (preview.type === 'html') {
    return (
      <div
        className="max-h-48 overflow-y-auto rounded-md px-2.5 py-2 text-[11px] text-slate-300"
        style={{
          background: 'rgba(255,255,255,0.04)',
          border: '1px solid rgba(255,255,255,0.08)',
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(255,255,255,0.1) transparent',
        }}
        dangerouslySetInnerHTML={{ __html: preview.html }}
      />
    );
  }

  /* ── JSON ── */
  if (preview.type === 'json') {
    return (
      <pre
        className="max-h-32 overflow-auto rounded-md px-2.5 py-2 text-[10px] text-slate-400"
        style={{
          background: 'rgba(0,0,0,0.3)',
          border: '1px solid rgba(255,255,255,0.08)',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-all',
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(255,255,255,0.1) transparent',
          fontFamily: 'monospace',
        }}
      >
        {preview.text}
      </pre>
    );
  }

  /* ── Düz metin ── */
  return (
    <div
      className="rounded-md px-2.5 py-2 text-[11px] text-slate-300"
      style={{
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.08)',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
      }}
    >
      {preview.text}
    </div>
  );
};

export default StarredEventPreview;
